'use client';

import { Ref, forwardRef, useImperativeHandle, useRef, useId, MouseEventHandler, useState } from 'react';
import styles from './search.module.scss';
import { SearchInput } from '@/ui-components/atoms/Inputs/SearchInput/SearchInput';
import { SearchResultList } from '@/ui-components/molecules/SearchResultList/SearchResultList';
import { type EntityListItemProps } from '@/ui-components/molecules/EntityListItem/EntityListItem';
import { Grid } from '@/ui-components/atoms/Layout/Grid/Grid';
import { EmptyState } from '@/ui-components/molecules/EmptyState/EmptyState';
import { Button } from '@/ui-components/atoms/Button/Button';

export interface SearchProps {
    value?: string;
    onChange?: (value: string) => void;
    searchResults?: EntityListItemProps[];
    isLoading?: boolean;
    minSearchLength?: number;
    maxSearchLength?: number;
    userTypingDelay?: number;
    onCancelClick?: MouseEventHandler;
}

const DATE = Date.now();

export const Search = forwardRef(function Search(
    {
        value = '',
        onChange = () => {},
        searchResults = [],
        isLoading = false,
        minSearchLength = 3,
        maxSearchLength = 128,
        userTypingDelay = 1000,
        onCancelClick,
    }: SearchProps,
    ref: Ref<HTMLInputElement>,
): React.JSX.Element {
    const inputRef = useRef<HTMLInputElement>(null);
    const searchUlId = useId();
    const hasResults = searchResults.length > 0;
    const showNoResults = !isLoading && value.trim().length >= minSearchLength && !hasResults;

    useImperativeHandle(ref, () => inputRef.current as HTMLInputElement);

    const [lastCount, setLastCount] = useState(0);

    if (searchResults.length > 0 && searchResults.length !== lastCount) {
        setLastCount(searchResults.length);
    }

    const searchCounts = searchResults.length > 0 ? searchResults.length : lastCount;

    return (
        <>
            <div data-searchresults={searchCounts} className={styles.wrapper} role="search">
                <Grid container className={styles.inputWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }} className={styles.inputWithButton}>
                        <SearchInput
                            ref={inputRef}
                            value={value}
                            onChange={onChange}
                            ariaExpanded={hasResults}
                            ariaControls={searchUlId}
                            userTypingDelay={userTypingDelay}
                            maxSearchLength={maxSearchLength}
                        />
                        <Button
                            className={styles.cancelButton}
                            variant="tertiary"
                            type="button"
                            size={'small'}
                            fitting={'hug-contents'}
                            onClick={onCancelClick}
                            aria-label="Close search"
                            tertiaryNoPadding
                        >
                            Close
                        </Button>
                    </Grid>
                </Grid>
            </div>
            {(showNoResults || hasResults) && (
                <Grid container className={styles.searchResultsWrapper}>
                    <Grid col={{ lg: 10 }} start={{ lg: 2 }} aria-live="polite">
                        {showNoResults ? (
                            <EmptyState
                                className={styles.emptyStateWrapper}
                                headline={`No results for "${value}"`}
                                subhead="Try with other search or search for articles by category through the links below."
                                image={
                                    <img
                                        src={`https://www.siriusxm.com/content/dam/sxm-com/lottie/stella/Stella-Dig-Animation.gif?${DATE}`}
                                        alt=""
                                    />
                                }
                            />
                        ) : (
                            <SearchResultList id={searchUlId} items={searchResults} itemsPerPage={7} />
                        )}
                    </Grid>
                </Grid>
            )}
        </>
    );
});

Search.displayName = 'Search';
